import { useEffect, useState } from "react";
import { api, extractError } from "@/lib/apiClient";
import { toast } from "sonner";
import { X, CheckCircle2, Loader2, ClipboardList, Sparkles } from "lucide-react";

const MODE_LABELS = { self_paced: "Self-paced", live_group: "Live group", one_to_one: "One-to-one" };

/** Doctor picks up to 5 published therapies, adds an optional note, hits
 * Send — the patient receives an email + sees the plan on their Home. */
export default function PrescribeModal({ bookingId, patientName, onClose, onDone }) {
  const [therapies, setTherapies] = useState([]);
  const [selected, setSelected] = useState([]);
  const [note, setNote] = useState("");
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState(false);
  const [alreadyPrescribed, setAlreadyPrescribed] = useState([]);

  useEffect(() => {
    api.get("/datp/therapies?limit=200").then((r) => setTherapies(r.data)).catch(() => {});
    api.get(`/bookings/${bookingId}/prescription`).then((r) => {
      const existing = (r.data.therapies || []).map((t) => t.id);
      setSelected(existing);
      setAlreadyPrescribed(existing);
      if (r.data.doctor_note) setNote(r.data.doctor_note);
    }).catch(() => {});
  }, [bookingId]);

  const toggle = (id) => {
    setSelected((sel) =>
      sel.includes(id) ? sel.filter((x) => x !== id) : sel.length >= 5 ? sel : [...sel, id]
    );
  };

  const submit = async () => {
    if (!selected.length) return toast.error("Pick at least one therapy");
    setBusy(true);
    try {
      const r = await api.post(`/bookings/${bookingId}/prescribe-therapies`, {
        therapy_ids: selected, doctor_note: note.trim() || null,
      });
      toast.success(`${r.data.count} therapy plan sent to ${patientName || "the patient"}.`);
      onDone?.();
    } catch (e) { toast.error(extractError(e)); }
    finally { setBusy(false); }
  };

  const filtered = therapies.filter((t) => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (
      t.title?.toLowerCase().includes(q) ||
      t.category_name?.toLowerCase().includes(q) ||
      t.subcategory?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" onClick={onClose} data-testid="prescribe-modal">
      <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[92vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 pb-4 flex items-start justify-between border-b border-[color:var(--mano-border)]">
          <div>
            <span className="uppercase-label inline-flex items-center gap-1">
              <ClipboardList size={13} /> Care plan for the week
            </span>
            <h2 className="font-display text-2xl mt-1">
              Prescribe therapies {patientName ? `for ${patientName}` : ""}
            </h2>
            <p className="text-xs text-[color:var(--mano-ink-soft)] mt-1">
              Pick up to 5 published therapies. The patient will receive an email + see them on their Home.
            </p>
          </div>
          <button onClick={onClose} data-testid="prescribe-close" className="p-1"><X size={18} /></button>
        </div>

        <div className="p-6 pt-4 space-y-4 flex-1 overflow-y-auto">
          <input
            data-testid="prescribe-search"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search title or category…"
            className="w-full h-11 px-3 rounded-lg border border-[color:var(--mano-border)] bg-white text-sm focus:outline-none focus:border-[color:var(--mano-primary)]"
          />

          {filtered.length === 0 ? (
            <div className="text-center text-sm text-[color:var(--mano-ink-soft)] py-8">
              No published therapies match.
            </div>
          ) : (
            <div className="space-y-2" data-testid="prescribe-list">
              {filtered.map((t) => {
                const on = selected.includes(t.id);
                const wasAlready = alreadyPrescribed.includes(t.id);
                return (
                  <button
                    key={t.id}
                    data-testid={`prescribe-item-${t.id}`}
                    onClick={() => toggle(t.id)}
                    disabled={!on && selected.length >= 5}
                    className={`w-full text-left rounded-xl border p-3 flex items-center gap-3 transition-colors ${
                      on
                        ? "border-[color:var(--mano-primary)] bg-[color:var(--mano-primary)]/5"
                        : "border-[color:var(--mano-border)] hover:border-[color:var(--mano-primary)]/40"
                    } disabled:opacity-60 disabled:cursor-not-allowed`}
                  >
                    <div className={`h-6 w-6 rounded-md border-2 flex items-center justify-center shrink-0 ${
                      on ? "bg-[color:var(--mano-primary)] border-[color:var(--mano-primary)] text-white" : "border-[color:var(--mano-border)]"
                    }`}>
                      {on && <CheckCircle2 size={13} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold flex items-center gap-2 flex-wrap">
                        <span>{t.title}</span>
                        {wasAlready && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 font-semibold">EXISTING</span>}
                      </div>
                      <div className="text-[11px] text-[color:var(--mano-ink-soft)]">
                        {t.category_name}{t.subcategory ? " · " + t.subcategory : ""} · {MODE_LABELS[t.mode]}{t.duration_min ? " · " + t.duration_min + "m" : ""} · {(t.language || "EN").toUpperCase()}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="p-6 pt-3 border-t border-[color:var(--mano-border)] space-y-3">
          <label className="block">
            <span className="text-xs font-semibold text-[color:var(--mano-ink-soft)]">Personal note (optional)</span>
            <textarea
              data-testid="prescribe-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="A gentle note the patient sees in their email…"
              className="mt-1 w-full h-20 px-3 py-2 rounded-lg border border-[color:var(--mano-border)] bg-white text-sm focus:outline-none focus:border-[color:var(--mano-primary)]"
            />
          </label>
          <div className="flex items-center justify-between">
            <span className="text-xs text-[color:var(--mano-ink-soft)]">
              {selected.length} of 5 picked
            </span>
            <button
              data-testid="prescribe-send"
              onClick={submit}
              disabled={busy || !selected.length}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[color:var(--mano-primary)] hover:bg-[color:var(--mano-primary-hover)] text-white text-sm font-semibold transition-colors disabled:opacity-60"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
              {busy ? "Sending…" : "Send care plan"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
